import { INavigatorAction, NavigatorActionType } from '@engoo/realtime-protocol/navigator';
import { useAnchorScrolling } from 'app/components/common/AnchorScrollingContext';
import { useActiveRoute } from 'app/components/router/ActiveRoute';
import { useHistory, useHistoryLocation } from 'app/components/router/History';
import NavigationError from 'app/constants/NavigationError';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { NavigationChange, NavigatorState } from 'app/models/navigator/reducer';
import {
  BookingQueryParam,
  BookingState,
  BookingType,
  BookingTypeSettings,
  ILessonBookingNavigation,
} from 'app/models/rtc/booking/constants';
import { SyncState } from 'app/models/rtc/sync/constants';
import { History } from 'app/services/history';
import { parseUrlLoose } from 'app/utils/LooseURL';
import isEqual from 'lodash/isEqual';
import QueryString from 'query-string';
import { useEffect, useMemo, useRef } from 'react';
import { getBasePath } from 'app/services/basePath';
import { useSyncNavigator } from 'app/containers/call/SyncNavigatorContext';
import { useLastSyncedTimestamp, useLastSyncedLocationKey } from 'app/containers/call/SyncNavigatorPersistence';
import * as t from 'io-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold } from 'fp-ts/lib/Either';

/**
 * Extra state to add to the local history entries when they
 * originate from synced navigation. This is to prevent them to be
 * synced back again to the server.
 */
interface ISyncedLocationState {
  /**
   * When this flag is set, the sync navigator will ignore the current location
   * and will attempt to move a user up to date with the last navigation in
   * the document.
   *
   * Use this when joining a call to be brought up to date with
   * everyone else on the call.
   */
  navigateToLastSync?: boolean;
}

const SyncedLocationStateDecoder: t.Decoder<unknown, ISyncedLocationState> = t.partial({
  navigateToLastSync: t.boolean,
}).asDecoder();

export interface IPartialLocation {
  hash?: string;
  pathname?: string;
  search?: string;
}

interface ISyncedLocation extends IPartialLocation {
  key?: string;
  state?: unknown;
}

export interface IDeps {
  /**
   * Base path (prefix) to subtract from all paths sent upstream
   * and add to all paths received from the server
   */
  basePath: string;

  /**
   * History service. Type reprents minimum used API surface.
   */
  history:
    & Pick<History, 'push' | 'replace'>
    & { location: Pick<History['location'], 'key'> };

  /**
   * Push a change to the document
   */
  addNavigationChange: (change: INavigatorAction) => void;

  /**
   * Scroll to an anchor on a page. Only activated if there's a
   * hash at the of the url.
   */
  scrollToAnchor: (anchor: string) => void;

  /**
   * Save last-synced timestamp. This should be permanent for the
   * duration of session (like session storage).
   */
  setLastSyncedTimestamp: (timestamp: number) => void;

  setLastSyncedLocationKey: (key: string) => void;

  /**
   * Set error to be displayed oto the user. Most common errors
   * here can be ones caused by browsers limiting how many times
   * we can update history within a period of time (like Safari)
   */
  setError: (error: NavigationError | undefined) => any;
}

export interface IProps {
  bookingState?: BookingState;

  /**
   * A subset of location parameters, only the ones that matter
   * for syncing location
   */
  location: ISyncedLocation;

  /**
   * Whether the current location should be synced on the call
   */
  routeSyncable: boolean;

  /**
   * Whether the document is blocked from writing
   */
  documentBlocked: boolean;

  /**
   * State of the synced document
   */
  connectionState: SyncState;

  /**
   * Timestamp of the last navigation event that was applied
   * locally. Only newer navigation events will be applied.
   */
  lastSyncedTimestamp: number | null;

  /**
   *
   */
  lastSyncedLocationKey: string | null;

  navHistory: NavigatorState;
}

const formatUrl = ({ pathname, search, hash }: IPartialLocation) => {
  // Remove booking query string params
  const query = QueryString.parse(search || '');

  delete query[BookingQueryParam.BookingId];
  delete query[BookingQueryParam.BookingType];

  const newSearch = QueryString.stringify(query);

  return `${pathname || '/'}${newSearch ? `?${newSearch}` : ''}${hash || ''}`;
};

const getBookingMaterialUrl = (navHistory: NavigatorState, bookingState?: BookingState) => {
  if (bookingState?.bookingType !== BookingType.Lesson) {
    return null;
  }

  // Get the last navigation event with a defined tag.
  const lastMaterialUpdate = navHistory.findLast((nav) => nav.tag !== undefined);
  // If the last material update has the same tag, return null
  // else return the material url.
  if (lastMaterialUpdate && isEqual(lastMaterialUpdate.tag, bookingState.navigation.tag)) {
    return null;
  } else {
    return bookingState.navigation;
  }
};

const navigateToLessonBookingMaterial = (
  {
    basePath,
    addNavigationChange,
    setError,
  }: IDeps,
  navigation: ILessonBookingNavigation,
) => {
  const url = formatUrl(navigation.url).substr(basePath.length);

  try {
    // Add the updated material url to the document
    addNavigationChange({
      url,
      tag: navigation.tag,
      type: NavigatorActionType.Navigate,
    });

    setError(undefined);
  } catch (error) {
    setError(new NavigationError({
      url,
      method: 'PUSH',
      cause: error,
    }));
  }
};

const pushRemoteAddressToLocalHistory = (
  {
    basePath,
    history,
    scrollToAnchor,
    setLastSyncedTimestamp,
    setLastSyncedLocationKey,
    setError,
  }: IDeps,
  lastNavigation: NavigationChange,
  localUrl: string | null,
  lastSyncedTimestamp: number | null,
  navigateToLastSync: boolean,
) => {
  if (
    lastSyncedTimestamp === null ||
    lastNavigation.timestamp > lastSyncedTimestamp ||
    navigateToLastSync
  ) {
    try {
      const { pathname, search, hash } = parseUrlLoose(lastNavigation.url);

      setLastSyncedTimestamp(lastNavigation.timestamp);

      if (localUrl !== lastNavigation.url) {
        const newLocation = {
          pathname: `${basePath}${pathname}`,
          search,
          hash,
        };

        // If we are navigating to the last sync event, we want to use
        // replace explicitly.
        if (navigateToLastSync) {
          history.replace(newLocation);
        } else {
          history.push(newLocation);
        }
      }

      // Obtain fresh key, directly from history because location
      // prop that's passed to the component won't be updated until
      // next render
      const key = history.location.key;
      if (key) {
        setLastSyncedLocationKey(key);
      }

      if (hash.substring(1)) {
        scrollToAnchor(hash.substring(1));
      }
    } catch (error) {
      setError(new NavigationError({
        url: lastNavigation.url,
        method: navigateToLastSync ? 'REPLACE' : 'PUSH',
        cause: error,
      }));
    }
  }
};

const pushLocalAddressToRemote = (
  { addNavigationChange, setLastSyncedLocationKey, setError }: IDeps,
  { key, url, syncedKey }: { url?: string, key?: string, syncedKey?: string },
) => {
  if (
    // Sync only if key is not identical to syncedKey
    syncedKey === key ||
    // Don't sync if we don't have the key or url set
    key === undefined ||
    url === undefined
  ) {
    return;
  }

  try {
    setLastSyncedLocationKey(key);
    addNavigationChange({
      type: NavigatorActionType.Navigate,
      url,
    });
    setError(undefined);
  } catch (error) {
    setError(new NavigationError({
      url,
      method: 'PUSH',
      cause: error,
    }));
  }
};

export const useSyncNavigatorSideEffects = (
  {
    location, routeSyncable, documentBlocked, navHistory, bookingState, lastSyncedTimestamp, lastSyncedLocationKey,
    ...deps
  }: IProps & IDeps,
) => {
  const hasBooking = Boolean(bookingState);
  const shouldSync = hasBooking && !documentBlocked;
  const navigateToLastSync: boolean = pipe(
    SyncedLocationStateDecoder.decode(location.state),
    fold(() => false, (state) => state.navigateToLastSync ?? false),
  );
  const lastNavigation = navHistory.last();

  // Use ref to all deps (that are mostly callbacks) to be on the
  // safe side and not cause any infinite loops if something is
  // not memoized
  const latestDeps = useRef<IDeps>(deps);
  latestDeps.current = deps;

  const localUrl = useMemo(() => {
    const url = formatUrl(location);

    if (!url.startsWith(deps.basePath)) {
      return null;
    }

    return url.substr(deps.basePath.length);
  }, [location, deps.basePath]);

  const bookingMaterial = useMemo(
    () => getBookingMaterialUrl(navHistory, bookingState),
    [bookingState, navHistory],
  );

  useEffect(() => {
    if (!shouldSync || !bookingMaterial) {
      return;
    }

    navigateToLessonBookingMaterial(latestDeps.current, bookingMaterial);
  }, [shouldSync, bookingMaterial]);

  useEffect(() => {
    if (!shouldSync || !routeSyncable || !localUrl || navigateToLastSync) {
      return;
    }

    pushLocalAddressToRemote(latestDeps.current, {
      url: localUrl,
      key: location.key,
      syncedKey: lastSyncedLocationKey || undefined,
    });
  }, [shouldSync, routeSyncable, localUrl, location.key, lastSyncedLocationKey, navigateToLastSync]);

  useEffect(() => {
    if (!lastNavigation || !hasBooking) {
      return;
    }

    pushRemoteAddressToLocalHistory(
      latestDeps.current,
      lastNavigation,
      localUrl,
      lastSyncedTimestamp,
      navigateToLastSync,
    );
  }, [
    hasBooking, lastSyncedTimestamp, lastNavigation, localUrl, navigateToLastSync,
  ]);
};

/**
 * Synchronises navigation between all peers when the call is
 * active. Cares only about the pathname, query string, and anchor
 * hashtag, allowing peers to be on different domains.
 *
 * Some browsers, esp. Safari has a limit as to how many times history
 * can change, currently 100 in last 30s. Other browsers have storage
 * limits so it's possible that navigating causes an error. If it
 * happens, a new `NavigationError` is set on the store. The error
 * will be cleared upon next successful navigation.
 *
 * @param {Object} props
 * @param {Object} props.history - history implementation
 */
const SyncNavigator = () => {
  const sharedAnchorCtrl = useAnchorScrolling();
  const history = useHistory();
  const location = useHistoryLocation();
  const { route } = useActiveRoute();
  const bookingState = useBookingState();

  const rtcRoom = bookingState?.rtcRoom || null;
  const routeSyncable = (
    bookingState &&
    BookingTypeSettings[bookingState.bookingType].allowSyncNavigation &&
    route
  ) ? route.syncable : false;

  const [lastSyncedTimestamp, setLastSyncedTimestamp] = useLastSyncedTimestamp(rtcRoom);
  const [lastSyncedLocationKey, setLastSyncedLocationKey] = useLastSyncedLocationKey(rtcRoom);

  const { documentState, setError, addNavigationChange, isBlocked, syncState } = useSyncNavigator();

  const scrollToAnchor = useMemo(() => {
    return sharedAnchorCtrl?.scrollToAnchor ?? (() => {});
  }, [sharedAnchorCtrl?.scrollToAnchor]);

  useSyncNavigatorSideEffects({
    basePath: getBasePath(),
    bookingState,
    location,
    history,
    scrollToAnchor,
    routeSyncable,
    lastSyncedTimestamp,
    lastSyncedLocationKey,
    setLastSyncedTimestamp,
    setLastSyncedLocationKey,
    setError,
    navHistory: documentState,
    addNavigationChange,
    documentBlocked: isBlocked,
    connectionState: syncState,
  });

  return null;
};

export default SyncNavigator;
